import Phaser from 'phaser';
import { SCENE_KEYS, GAME_CONFIG } from '@/config/constants';
import { getGlobalLeaderboard, getStageLeaderboard, LeaderboardEntry, StageScoreRecord } from '@/utils/SupabaseClient';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { COLORS, COLORS_HEX, FONTS, RADIUS, TEXT_PRESET } from '@/ui/theme';
import { GamepadNav } from '@/utils/GamepadNav';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';

/**
 * Leaderboard Scene — Code-drawn glassmorphism UI matching MenuScene style
 */
export class LeaderboardScene extends Phaser.Scene {
  private activeTab: 'global' | 'stage' = 'global';
  private selectedStage: number = 1;
  private entriesContainer?: Phaser.GameObjects.Container;
  private loadingText?: Phaser.GameObjects.Text;
  private highlightScore?: number;
  private tabContainer?: Phaser.GameObjects.Container;
  private nav?: GamepadNav;
  private loadGeneration = 0;

  constructor() {
    super({ key: SCENE_KEYS.LEADERBOARD });
  }

  update(): void {
    this.nav?.update();
  }

  init(data?: { tab?: 'global' | 'stage'; highlightScore?: number }): void {
    this.activeTab = data?.tab || 'global';
    this.highlightScore = data?.highlightScore;
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    fadeIn(this, 400);

    const videoKey = 'bg_video_world1_video';
    if (this.cache.video.has(videoKey)) {
      const videoBg = this.add.video(cx, cy, videoKey);
      videoBg.setDepth(0).setScale(1).setVolume(0).play(true).setAlpha(0.58);
    }

    const bg = this.add.graphics().setDepth(1);
    bg.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.deepOcean, COLORS.deepOcean, 0.9, 0.9, 0.98, 0.98);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    const glows = this.add.graphics().setDepth(2);
    glows.fillStyle(COLORS.aqua, 0.1);
    glows.fillCircle(cx, cy - 220, 260);
    glows.fillStyle(COLORS.sapphire, 0.12);
    glows.fillCircle(cx - 440, cy + 60, 260);
    glows.fillCircle(cx + 440, cy + 60, 260);

    // Vignette
    const vig = this.add.graphics().setDepth(3);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.78, 0.78, 0, 0).fillRect(0, 0, WIDTH, 150);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.82, 0.82).fillRect(0, HEIGHT - 150, WIDTH, 150);

    // === MAIN PANEL (rounded) ===
    const pw = 1300, ph = 820, pRadius = RADIUS.xl;
    const plx = cx - pw / 2;
    const pty = 60;

    const panel = this.add.graphics().setDepth(4);
    panel.fillStyle(COLORS.aqua, 0.08);
    panel.fillRoundedRect(plx - 12, pty - 10, pw + 24, ph + 24, pRadius + 10);
    panel.fillStyle(COLORS.midnightDepth, 0.5);
    panel.fillRoundedRect(plx, pty + 8, pw, ph, pRadius);
    panel.fillGradientStyle(COLORS.tidewater, COLORS.tidewater, COLORS.abyssBlue, COLORS.abyssBlue, 0.94, 0.94, 0.92, 0.92);
    panel.fillRoundedRect(plx, pty, pw, ph, pRadius);
    panel.fillStyle(COLORS.pearlWhite, 0.06);
    panel.fillRoundedRect(plx + 10, pty + 10, pw - 20, 118, pRadius - 10);
    panel.lineStyle(2, COLORS.aqua, 0.62);
    panel.strokeRoundedRect(plx, pty, pw, ph, pRadius);
    panel.lineStyle(1, COLORS.pearlWhite, 0.2);
    panel.strokeRoundedRect(plx + 4, pty + 4, pw - 8, ph - 8, pRadius - 4);
    panel.fillStyle(COLORS.aquaLight, 0.34);
    panel.fillRoundedRect(plx + 74, pty + 4, pw - 148, 3, 2);

    // === TITLE ===
    this.add.text(cx, pty + 66, 'HALL OF FAME', {
      ...TEXT_PRESET.section,
      fontFamily: FONTS.fancy,
      fontSize: '52px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    this.add.text(cx, pty + 104, 'Fresh-run records only. Stage clears flow straight to the next dive.', {
      ...TEXT_PRESET.body,
      fontSize: '23px',
      color: COLORS_HEX.textSecondary,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    // Divider
    const divG = this.add.graphics().setDepth(5);
    const divY = pty + 136;
    divG.lineStyle(1, COLORS.aqua, 0.35);
    divG.lineBetween(cx - 280, divY, cx + 280, divY);
    divG.fillStyle(COLORS.aqua, 0.5);
    divG.fillTriangle(cx - 5, divY, cx, divY - 4, cx + 5, divY);
    divG.fillTriangle(cx - 5, divY, cx, divY + 4, cx + 5, divY);

    // === TABS ===
    this.tabContainer = this.add.container(0, 0).setDepth(5);
    this.createTabs(cx, pty + 188);

    // === ENTRIES CONTAINER ===
    this.entriesContainer = this.add.container(0, 0).setDepth(4);

    // Loading
    this.loadingText = this.add.text(cx, HEIGHT / 2, 'Loading...', {
      ...TEXT_PRESET.section,
      fontSize: '36px',
      color: COLORS_HEX.aquaLight,
    }).setOrigin(0.5).setDepth(5);

    // === BACK BUTTON ===
    const backBtn = createPillButton(this, {
      x: cx, y: pty + ph + 55,
      label: 'RETURN TO MENU',
      variant: 'secondary',
      size: 'medium',
      width: 440,
      onClick: () => transitionTo(this, SCENE_KEYS.MENU),
    });
    backBtn.setDepth(5);

    // Gamepad: Left/Right toggle GLOBAL/PER STAGE; Up/Down adjust stage when on PER STAGE;
    // A or B → return to menu; Start → return to menu.
    const back = () => transitionTo(this, SCENE_KEYS.MENU);
    const switchTab = (tab: 'global' | 'stage') => {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.createTabs(cx, pty + 188);
      this.loadData();
    };
    const bumpStage = (delta: number) => {
      if (this.activeTab !== 'stage') return;
      const next = Phaser.Math.Clamp(this.selectedStage + delta, 1, 7);
      if (next === this.selectedStage) return;
      this.selectedStage = next;
      this.createTabs(cx, pty + 188);
      this.loadData();
    };
    this.nav = new GamepadNav(this, {
      onLeft:  () => switchTab('global'),
      onRight: () => switchTab('stage'),
      onUp:    () => bumpStage(-1),
      onDown:  () => bumpStage(1),
      onConfirm: back,
      onCancel:  back,
      onPause:   back,
    });

    // Selection halo on the lone RETURN TO MENU CTA — visible only while a
    // gamepad is connected (any face button goes back, so the joystick is
    // always "on" it).
    const backHalo = this.add.graphics().setDepth(6);
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      if (!backBtn.active) { backHalo.clear(); return; }
      if (this.nav?.isPadConnected()) {
        drawSelectionHalo(backHalo, {
          cx: backBtn.x,
          cy: backBtn.y,
          w: ((backBtn as any).pillWidth ?? 440) * backBtn.scaleX,
          h: ((backBtn as any).pillHeight ?? 84) * backBtn.scaleY,
          rx: ((backBtn as any).pillCapRx ?? 42) * backBtn.scaleX,
          ry: ((backBtn as any).pillCapRy ?? 42) * backBtn.scaleY,
        }, { gap: 14 });
      } else {
        backHalo.clear();
      }
    });

    // Load data
    this.loadData();
  }

  private createTabs(cx: number, y: number): void {
    if (!this.tabContainer) return;
    this.tabContainer.removeAll(true);

    const makeTab = (x: number, label: string, active: boolean, onClick: () => void) => {
      const btn = createPillButton(this, {
        x, y, label,
        variant: 'primary',
        size: 'small',
        width: 240, height: 56,
        onClick,
      });
      // Inactive tabs render the same cyan pill but dimmed — keeps the
      // canonical PLAY silhouette while still reading as "not selected".
      if (!active) {
        btn.setAlpha(0.55);
      } else {
        const glow = this.add.graphics();
        glow.lineStyle(3, COLORS.aquaLight, 0.95);
        glow.strokeRoundedRect(x - 124, y - 32, 248, 64, 32);
        glow.lineStyle(1, 0xFFFFFF, 0.6);
        glow.strokeRoundedRect(x - 120, y - 28, 240, 56, 28);
        this.tabContainer!.add(glow);
      }
      this.tabContainer!.add(btn);
    };

    makeTab(cx - 150, 'GLOBAL', this.activeTab === 'global', () => {
      this.activeTab = 'global';
      this.createTabs(cx, y);
      this.loadData();
    });

    makeTab(cx + 150, 'PER STAGE', this.activeTab === 'stage', () => {
      this.activeTab = 'stage';
      this.createTabs(cx, y);
      this.loadData();
    });

    // Stage selector for per-stage tab
    if (this.activeTab === 'stage') {
      const selY = y + 48;
      const stageLabel = this.add.text(cx, selY, `Stage ${this.selectedStage}`, {
        ...TEXT_PRESET.label,
        fontSize: '28px',
        color: COLORS_HEX.aquaLight,
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);
      this.tabContainer.add(stageLabel);

      const leftArr = this.add.text(cx - 90, selY, '\u25C0', {
        ...TEXT_PRESET.label,
        fontSize: '24px',
        color: COLORS_HEX.goldLight,
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5).setInteractive({ useHandCursor: true });
      leftArr.on('pointerdown', () => {
        this.selectedStage = Math.max(1, this.selectedStage - 1);
        stageLabel.setText(`Stage ${this.selectedStage}`);
        this.loadData();
      });
      leftArr.on('pointerover', () => leftArr.setColor(COLORS_HEX.goldLight));
      leftArr.on('pointerout', () => leftArr.setColor(COLORS_HEX.gold));
      this.tabContainer.add(leftArr);

      const rightArr = this.add.text(cx + 90, selY, '\u25B6', {
        ...TEXT_PRESET.label,
        fontSize: '24px',
        color: COLORS_HEX.goldLight,
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5).setInteractive({ useHandCursor: true });
      rightArr.on('pointerdown', () => {
        this.selectedStage = Math.min(7, this.selectedStage + 1);
        stageLabel.setText(`Stage ${this.selectedStage}`);
        this.loadData();
      });
      rightArr.on('pointerover', () => rightArr.setColor(COLORS_HEX.goldLight));
      rightArr.on('pointerout', () => rightArr.setColor(COLORS_HEX.gold));
      this.tabContainer.add(rightArr);
    }
  }

  private async loadData(): Promise<void> {
    const myGen = ++this.loadGeneration;
    const tabAtStart = this.activeTab;
    const stageAtStart = this.selectedStage;

    if (this.entriesContainer) this.entriesContainer.removeAll(true);
    if (this.loadingText) this.loadingText.setText('SYNCING RECORDS...').setVisible(true);

    if (tabAtStart === 'global') {
      const entries = await getGlobalLeaderboard(10);
      if (myGen !== this.loadGeneration || !this.entriesContainer?.active) return;
      this.displayGlobalEntries(entries);
    } else {
      const entries = await getStageLeaderboard(stageAtStart, 10);
      if (myGen !== this.loadGeneration || !this.entriesContainer?.active) return;
      this.displayStageEntries(entries);
    }

    if (this.loadingText) this.loadingText.setVisible(false);
  }

  private displayGlobalEntries(entries: LeaderboardEntry[]): void {
    if (!this.entriesContainer) return;
    const { WIDTH } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const startY = this.activeTab === 'stage' ? 354 : 304;
    const rowH = 52;
    const tableW = 1100;

    this.drawHeader(cx, startY, tableW, 'STAGE');

    if (entries.length === 0) {
      this.drawEmptyMessage(cx, startY + 80, 'No scores yet. Be the first to set the current.');
      return;
    }

    entries.forEach((entry, i) => {
      const y = startY + 65 + i * rowH;
      const rank = i + 1;
      const isHighlight = this.highlightScore !== undefined && entry.best_score === this.highlightScore;
      this.drawRow(cx, y, tableW, rowH, rank, isHighlight, i,
        entry.player_name,
        entry.best_score.toLocaleString(),
        String(entry.highest_stage)
      );
    });
  }

  private displayStageEntries(entries: StageScoreRecord[]): void {
    if (!this.entriesContainer) return;
    const { WIDTH } = GAME_CONFIG;
    const cx = WIDTH / 2;
    // Per-stage tab sits 50px lower than global because the stage selector
    // arrows live above the table. Tighten the row pitch (46 vs the global
    // tab's 52) so the 10th row still ends inside the panel's 820px height.
    const startY = 354;
    const rowH = 46;
    const tableW = 1100;

    this.drawHeader(cx, startY, tableW, 'TIME');

    if (entries.length === 0) {
      this.drawEmptyMessage(cx, startY + 80, `No scores for Stage ${this.selectedStage} yet. Claim it.`);
      return;
    }

    entries.forEach((entry, i) => {
      const y = startY + 56 + i * rowH;
      const rank = i + 1;
      this.drawRow(cx, y, tableW, rowH, rank, false, i,
        entry.player_name,
        entry.stage_score.toLocaleString(),
        `${entry.time_remaining}s`
      );
    });
  }

  // ── Shared drawing helpers ──

  // Column anchors (left edge of table = cx - w/2).
  // rank + name are left-aligned (origin 0); score + last are right-aligned
  // (origin 1) so multi-digit numbers line up on a clean right edge.
  private getColumns(cx: number, w: number) {
    const left = cx - w / 2;
    return {
      rank: left + 60,
      name: left + 160,
      scoreRight: left + 800,
      lastRight: left + 1050,
    };
  }

  private drawHeader(cx: number, y: number, w: number, lastLabel: string): void {
    const cols = this.getColumns(cx, w);

    // Dark strip
    const bar = this.add.graphics();
    bar.fillStyle(COLORS.midnightDepth, 0.76);
    bar.fillRoundedRect(cx - w / 2, y - 24, w, 48, 6);
    bar.lineStyle(1, COLORS.aqua, 0.28);
    bar.strokeRoundedRect(cx - w / 2, y - 24, w, 48, 6);
    this.entriesContainer!.add(bar);

    // Accent lines
    const lines = this.add.graphics();
    lines.lineStyle(1, COLORS.aquaLight, 0.36);
    lines.lineBetween(cx - w / 2 + 10, y - 24, cx + w / 2 - 10, y - 24);
    lines.lineBetween(cx - w / 2 + 10, y + 24, cx + w / 2 - 10, y + 24);
    this.entriesContainer!.add(lines);

    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      ...TEXT_PRESET.label,
      fontSize: '28px',
      color: COLORS_HEX.aquaLight,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 3,
    };

    const addCol = (x: number, text: string, originX: number) => {
      const t = this.add.text(x, y, text, style).setOrigin(originX, 0.5);
      this.entriesContainer!.add(t);
    };

    addCol(cols.rank - 20, '#', 0);
    addCol(cols.name, 'NAME', 0);
    addCol(cols.scoreRight, 'SCORE', 1);
    addCol(cols.lastRight, lastLabel, 1);
  }

  private drawRow(
    cx: number, y: number, w: number, h: number,
    rank: number, isHighlight: boolean, index: number,
    name: string, score: string, lastCol: string
  ): void {
    const cols = this.getColumns(cx, w);

    // Zebra stripe
    if (index % 2 === 0) {
      const zebra = this.add.graphics();
      zebra.fillStyle(COLORS.deepOcean, 0.3);
      zebra.fillRoundedRect(cx - w / 2, y - h / 2 + 2, w, h - 4, 4);
      this.entriesContainer!.add(zebra);
    }

    // Highlight bg
    if (isHighlight) {
      const hlBg = this.add.graphics();
      hlBg.fillStyle(COLORS.aqua, 0.15);
      hlBg.fillRoundedRect(cx - w / 2, y - h / 2 + 2, w, h - 4, 4);
      hlBg.lineStyle(1, COLORS.aqua, 0.4);
      hlBg.strokeRoundedRect(cx - w / 2, y - h / 2 + 2, w, h - 4, 4);
      this.entriesContainer!.add(hlBg);
    }

    // Rank color
    let color: string = COLORS_HEX.textPrimary;
    if (isHighlight) color = COLORS_HEX.victoryGreen;
    else if (rank === 1) color = COLORS_HEX.goldLight;
    else if (rank === 2) color = COLORS_HEX.aquaLight;
    else if (rank === 3) color = COLORS_HEX.pearlShadow;

    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONTS.display, fontSize: '28px', color: color,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 3,
    };

    // Code-drawn medal dot for top 3
    if (rank <= 3) {
      const dotColor = rank === 1 ? COLORS.goldLight : (rank === 2 ? COLORS.aquaLight : COLORS.pearlShadow);
      const dot = this.add.graphics();
      dot.fillStyle(dotColor, 0.9);
      dot.fillCircle(cols.rank - 30, y, 8);
      dot.lineStyle(1.5, 0xFFFFFF, 0.4);
      dot.strokeCircle(cols.rank - 30, y, 8);
      this.entriesContainer!.add(dot);
    }

    // Rank
    this.entriesContainer!.add(
      this.add.text(cols.rank - 20, y, `#${rank}`, style).setOrigin(0, 0.5)
    );
    // Name
    this.entriesContainer!.add(
      this.add.text(cols.name, y, name, style).setOrigin(0, 0.5)
    );
    // Score (right-aligned)
    this.entriesContainer!.add(
      this.add.text(cols.scoreRight, y, score, style).setOrigin(1, 0.5)
    );
    // Last column (right-aligned)
    this.entriesContainer!.add(
      this.add.text(cols.lastRight, y, lastCol, style).setOrigin(1, 0.5)
    );
  }

  private drawEmptyMessage(cx: number, y: number, text: string): void {
    const msg = this.add.text(cx, y, text, {
      ...TEXT_PRESET.section,
      fontSize: '32px',
      color: COLORS_HEX.textSecondary,
      strokeThickness: 2,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);
    this.entriesContainer!.add(msg);
  }
}
